import React from "react";
import { MdCloudUpload, MdDelete, MdImage } from "react-icons/md";
import Card from "components/card";
import LoadingSpinner from "components/common/LoadingSpinner";
import { Recipe } from "../../types/recipe";
import { getMealTypeLabel, getMealTypeColor } from "../../utils/recipeUtils";

interface RecipeHeaderCardProps {
  recipe: Recipe;
  uploadingImage: boolean;
  deletingImage: boolean;
  imageError: string | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onImageUploadClick: () => void;
  onDeleteImageClick: () => void;
  onImageFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// Image (with upload / delete overlay) plus name, meal type and description.
// The upload handlers stay in the view; this only renders their state.
const RecipeHeaderCard: React.FC<RecipeHeaderCardProps> = ({
  recipe,
  uploadingImage,
  deletingImage,
  imageError,
  fileInputRef,
  onImageUploadClick,
  onDeleteImageClick,
  onImageFileChange,
}) => {
  return (
    <Card extra="w-full p-3 md:p-6">
      <div className="flex flex-col items-start space-y-6 md:flex-row md:space-x-6 md:space-y-0">
        {/* Recipe Image Section */}
        <div className="flex-shrink-0">
          <div className="group relative">
            {recipe.recipe_image?.full_path ? (
              <>
                <img
                  src={recipe.recipe_image.full_path}
                  alt={recipe.name}
                  className="h-48 w-48 rounded-lg object-cover"
                />
                {/* Hover overlay for delete */}
                <div className="bg-black absolute inset-0 flex items-center justify-center space-x-2 rounded-lg bg-opacity-50 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    onClick={onImageUploadClick}
                    disabled={uploadingImage || deletingImage}
                    className="rounded-full bg-white p-2 transition-colors hover:bg-gray-100 disabled:opacity-50"
                    title="Change image"
                  >
                    <MdCloudUpload className="h-5 w-5 text-gray-700" />
                  </button>
                  <button
                    onClick={onDeleteImageClick}
                    disabled={uploadingImage || deletingImage}
                    className="rounded-full bg-red-600 p-2 transition-colors hover:bg-red-700 disabled:opacity-50"
                    title="Delete image"
                  >
                    <MdDelete className="h-5 w-5 text-white" />
                  </button>
                </div>
                {uploadingImage && (
                  <div className="bg-black absolute inset-0 flex items-center justify-center rounded-lg bg-opacity-50">
                    <LoadingSpinner bare size="lg" tone="white" />
                  </div>
                )}
              </>
            ) : (
              <div
                onClick={onImageUploadClick}
                className="flex h-48 w-48 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-100 transition-colors hover:bg-gray-200 dark:border-gray-600 dark:bg-navy-800 dark:hover:bg-navy-700"
              >
                {uploadingImage ? (
                  <LoadingSpinner bare size="lg" />
                ) : (
                  <>
                    <MdImage className="mb-2 h-12 w-12 text-gray-400" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Upload Image
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={onImageFileChange}
            className="hidden"
          />

          {/* Image error message */}
          {imageError && (
            <div className="mt-2 text-sm text-red-600 dark:text-red-400">
              {imageError}
            </div>
          )}

          {/* Image upload hint */}
          {!uploadingImage && (
            <div className="mt-2 w-full max-w-48 text-center text-xs text-gray-500 dark:text-gray-400">
              Click to upload JPG, JPEG, or PNG (max 20MB)
            </div>
          )}
        </div>

        {/* Recipe Info Section */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <h1 className="text-3xl font-bold text-navy-700 dark:text-white">
                  {recipe.name}
                </h1>
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${getMealTypeColor()}`}
                >
                  {getMealTypeLabel(recipe.meal_type)}
                </span>
              </div>
              {recipe.description && (
                <p className="mb-4 text-lg text-gray-600 dark:text-gray-400">
                  {recipe.description}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default RecipeHeaderCard;
